import React from 'react';
import { Link, NavLink } from 'react-router-dom';
import { AppLayout } from '@/components/layout/AppLayout';
import { cn } from '@/lib/utils';

interface LegalLayoutProps {
  children: React.ReactNode;
  title: string;
  lastUpdated: string;
}

export function LegalLayout({ children, title, lastUpdated }: LegalLayoutProps) {
  const linkClass = ({ isActive }: { isActive: boolean }) => 
    cn(
      "block px-4 py-3 rounded-lg font-label-caps text-[11px] uppercase tracking-widest transition-all",
      isActive 
        ? "bg-primary text-on-primary shadow-md" 
        : "text-on-surface-variant hover:bg-surface-container-high"
    );

  return (
    <AppLayout>
      <div className="flex-1 bg-surface-container-low overflow-y-auto">
        <header className="bg-surface border-b border-outline-variant px-4 md:px-margin-page py-16">
          <div className="max-w-max-width-doc mx-auto">
             <span className="font-label-caps text-label-caps text-primary uppercase tracking-widest mb-2 block">Legal & Regulations</span>
             <h1 className="font-h1 text-h1 text-on-surface mb-2">{title}</h1>
             <p className="font-body-md text-body-md text-on-surface-variant italic">
               Last revision: {lastUpdated}
             </p>
          </div>
        </header>

        <div className="px-4 md:px-margin-page py-12">
          <div className="max-w-max-width-doc mx-auto flex flex-col lg:flex-row gap-12">
            {/* Sidebar Navigation */}
            <aside className="w-full lg:w-64 shrink-0 space-y-2">
              <NavLink to="/terms" className={linkClass}>Terms of Service</NavLink>
              <NavLink to="/privacy" className={linkClass}>Privacy Policy</NavLink>
              <NavLink to="/cookies" className={linkClass}>Cookie Protocol</NavLink>
              
              <div className="mt-12 p-6 rounded-xl bg-surface-container-highest border border-outline-variant">
                <span className="material-symbols-outlined text-primary mb-2">gavel</span>
                <p className="text-[11px] font-body-md text-on-surface-variant leading-relaxed">
                  These regulations ensure the integrity of the DocForge Scriptorium and the protection of all digital manuscripts.
                </p>
              </div>
            </aside>

            {/* Content Area */}
            <main className="flex-1 bg-surface border border-outline-variant rounded-2xl p-8 md:p-12 shadow-sm ink-drop-shadow">
               <article className="doc-content prose prose-stone dark:prose-invert max-w-none">
                 {children}
               </article>
               
               <div className="mt-16 pt-8 border-t border-outline-variant/30 flex flex-col md:flex-row justify-between items-center gap-6">
                  <p className="text-[11px] text-on-surface-variant font-body-md italic">
                    By utilizing the Scriptorium, you acknowledge these protocols.
                  </p>
                  <button onClick={() => window.print()} className="flex items-center gap-2 font-label-caps text-[10px] text-primary uppercase tracking-widest hover:underline">
                    <span className="material-symbols-outlined text-[16px]">print</span>
                    Print Protocol
                  </button>
               </div>
            </main>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
